import React, { useState, useCallback, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  DeleteSweep as ClearIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  ChatSuggestion,
  JoinDefinition,
  TableSummary,
  ColumnSummary,
} from '../../types/unifiedSchema';
import { useSchemaChatState, ViewContext } from '../../hooks/useSchemaChatState';
import ChatMessageList from './ChatMessageList';

interface SchemaChatDrawerProps {
  open: boolean;
  onClose: () => void;
  batchId: string;
  summaries: TableSummary[];
  /** Current view context — which table / tab the user is looking at */
  viewContext?: ViewContext;
  onTogglePrimaryKey: (tableIndex: number, columnName: string, isPk: boolean) => void;
  onUpdateJoins: (tableIndex: number, updatedJoins: JoinDefinition[]) => void;
  onUpdateColumn: (tableIndex: number, colIndex: number, updates: Partial<ColumnSummary>) => void;
  onUpdateTable: (index: number, field: keyof TableSummary, value: string) => void;
  /** Called when the advisor agent modifies the schema via write tools */
  onSchemaModified?: () => void;
}

const DRAWER_WIDTH = 400;

const TAB_DISPLAY: Record<string, string> = {
  overview: 'Overview',
  columns: 'Columns',
  keys_joins: 'Keys & Joins',
  preview: 'Preview',
};

const SchemaChatDrawer: React.FC<SchemaChatDrawerProps> = ({
  open,
  onClose,
  batchId,
  summaries,
  viewContext,
  onTogglePrimaryKey,
  onUpdateJoins,
  onUpdateColumn,
  onUpdateTable,
  onSchemaModified,
}) => {
  const { messages, isTyping, sendMessage, setViewContext, updateSuggestionStatus, clearMessages } =
    useSchemaChatState(batchId, summaries, onSchemaModified);
  const [input, setInput] = useState('');

  // Keep the hook's ref in sync with the prop
  useEffect(() => {
    setViewContext(viewContext);
  }, [viewContext, setViewContext]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;
    sendMessage(trimmed);
    setInput('');
  }, [input, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const applySuggestion = useCallback(
    (suggestion: ChatSuggestion) => {
      const { type, payload } = suggestion;
      try {
        switch (type) {
          case 'set_pk': {
            const tableIndex = findTableIndex(summaries, payload.table);
            if (tableIndex >= 0) {
              onTogglePrimaryKey(tableIndex, payload.column, true);
            }
            break;
          }
          case 'add_relationship': {
            const tableIndex = findTableIndex(summaries, payload.table);
            if (tableIndex >= 0) {
              const existing = summaries[tableIndex].likely_joins || [];
              const newJoin: JoinDefinition = {
                on: Array.isArray(payload.on) ? payload.on : [payload.on],
                to: payload.to,
                join_type: payload.join_type || 'fk_candidate',
                confidence: payload.confidence || 0.8,
                description: payload.description || '',
              };
              onUpdateJoins(tableIndex, [...existing, newJoin]);
            }
            break;
          }
          case 'update_column': {
            const tableIndex = findTableIndex(summaries, payload.table);
            if (tableIndex >= 0) {
              const colIdx = summaries[tableIndex].column_summaries?.findIndex(
                (c) => c.name.toLowerCase() === (payload.column || '').toLowerCase()
              );
              if (colIdx !== undefined && colIdx >= 0) {
                const updates: Partial<ColumnSummary> = {};
                if (payload.semantic_role) updates.semantic_role = payload.semantic_role;
                if (payload.description) updates.description = payload.description;
                if (payload.pii_likelihood !== undefined) updates.pii_likelihood = payload.pii_likelihood;
                onUpdateColumn(tableIndex, colIdx, updates);
              }
            }
            break;
          }
          case 'set_entity_type': {
            const tableIndex = findTableIndex(summaries, payload.table);
            if (tableIndex >= 0) {
              onUpdateTable(tableIndex, 'entity_type', payload.entity_type);
            }
            break;
          }
        }
      } catch (err) {
        console.error('Failed to apply suggestion:', err);
      }
    },
    [summaries, onTogglePrimaryKey, onUpdateJoins, onUpdateColumn, onUpdateTable]
  );

  const handleApply = useCallback(
    (messageId: string, suggestionId: string) => {
      const msg = messages.find((m) => m.id === messageId);
      const suggestion = msg?.suggestions?.find((s) => s.id === suggestionId);
      if (suggestion) {
        applySuggestion(suggestion);
        updateSuggestionStatus(messageId, suggestionId, 'accepted');
      }
    },
    [messages, applySuggestion, updateSuggestionStatus]
  );

  const handleReject = useCallback(
    (messageId: string, suggestionId: string) => {
      updateSuggestionStatus(messageId, suggestionId, 'rejected');
    },
    [updateSuggestionStatus]
  );

  const ctxLabel = viewContext?.selectedTableSemanticName || viewContext?.selectedTable;
  const tabLabel = viewContext?.activeTab ? TAB_DISPLAY[viewContext.activeTab] ?? viewContext.activeTab : null;

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      sx={(theme) => {
        // Align drawer height with the main content area below the app header.
        // We assume a 64px header if mixins.toolbar is not defined.
        const toolbarHeight =
          (theme.mixins?.toolbar as any)?.minHeight ?? 64;
        return {
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: toolbarHeight,
            height: `calc(100% - ${toolbarHeight}px)`,
          },
        };
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            Schema Assistant
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={clearMessages} title="Clear chat">
              <ClearIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Context indicator — shows which table/tab the user is viewing */}
        {ctxLabel && (
          <Box
            sx={{
              px: 2,
              py: 0.75,
              bgcolor: 'grey.50',
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <ViewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              Viewing:
            </Typography>
            <Chip label={ctxLabel} size="small" variant="outlined" sx={{ maxWidth: 180 }} />
            {tabLabel && (
              <Chip label={tabLabel} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
            )}
          </Box>
        )}

        {/* Messages */}
        <ChatMessageList
          messages={messages}
          isTyping={isTyping}
          onApplySuggestion={handleApply}
          onRejectSuggestion={handleReject}
        />

        {/* Input */}
        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Ask about your schema..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isTyping}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!input.trim() || isTyping}
            >
              <SendIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};

// Helper to find table index by name
function findTableIndex(summaries: TableSummary[], name: string): number {
  if (!name) return -1;
  const lower = name.toLowerCase();
  return summaries.findIndex(
    (t) =>
      t.file_name.toLowerCase() === lower ||
      t.semantic_name?.toLowerCase() === lower
  );
}

export default SchemaChatDrawer;
export { DRAWER_WIDTH };
